import { Box, Center, Container, Flex, HStack, VStack } from "styled-system/jsx";
import { css } from "styled-system/css";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/id";

import { Button, Input } from "~/components";
import { useToast } from "~/context/Toast";
import { pos } from "~/services";
import type { PosActivityType } from "~/services";
import { normalizeIDR, removeMask } from "~/utils";

// Target streak mencatat kas (hanya untuk label & rasio progress bar).
const STREAK_TARGET = 30;
const NOTE_MAX_LENGTH = 255;
// Batas atas nominal (1 triliun). Tanpa ini, angka 20 digit lolos `Number.isInteger`
// tetapi disimpan sebagai angka lain karena melewati MAX_SAFE_INTEGER.
const AMOUNT_MAX = 1_000_000_000_000;

const CARD = {
  rounded: "24px",
  borderColor: "secondary.lighten2",
  borderWidth: "1px",
  borderStyle: "solid",
  p: "32px",
} as const;

const TYPE_OPTIONS: { value: PosActivityType; label: string }[] = [
  { value: "income", label: "Pemasukan" },
  { value: "expense", label: "Pengeluaran" },
];

const formatDate = (value: string) => dayjs(value).locale("id").format("DD MMMM YYYY");
const formatDateTime = (value: string) => dayjs(value).locale("id").format("DD MMMM YYYY, HH.mm");

// `http()` menolak dengan string pesan dari backend; tetap toleran bila
// suatu saat yang diterima berupa Error/objek.
const errorMessage = (e: unknown): string => {
  if (typeof e === "string" && e.trim()) return e;
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return "Terjadi kesalahan, coba lagi.";
};

export const CatatanKasir = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form catat transaksi. `amount` disimpan sebagai digit murni (tanpa pemisah ribuan).
  const [type, setType] = useState<PosActivityType>("income");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const activitiesQuery = useQuery({
    queryKey: ["pos", "activities"],
    queryFn: () => pos.getActivities(),
  });

  const summaryQuery = useQuery({
    queryKey: ["pos", "summary"],
    queryFn: () => pos.getSummary(),
  });

  const streakQuery = useQuery({
    queryKey: ["pos", "streak"],
    queryFn: () => pos.getStreak(),
  });

  const activities = useMemo(() => activitiesQuery.data?.data ?? [], [activitiesQuery.data]);
  const today = summaryQuery.data?.data?.today;
  // Rekap net per hari kalender WIB (maks. 30 hari), dikirim server pada respons
  // yang sama dengan `today` — tidak perlu request tambahan.
  const daily = useMemo(() => summaryQuery.data?.data?.daily ?? [], [summaryQuery.data]);
  const streak = streakQuery.data?.data;

  const amountNumber = Number(amount);
  const amountError =
    amount.length === 0 || !Number.isInteger(amountNumber) || amountNumber <= 0
      ? "Masukkan angka bulat lebih dari 0."
      : amountNumber > AMOUNT_MAX
        ? "Nominal maksimal Rp1.000.000.000.000 (1 triliun)."
        : null;
  const isAmountValid = amountError === null;

  const createMutation = useMutation({
    mutationFn: () =>
      pos.create({
        amount: amountNumber,
        type,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      }),
    onSuccess: (res) => {
      if (!res.success) {
        toast.error({
          title: "Gagal menyimpan",
          description: (res.message as { message?: string })?.message ?? "Terjadi kesalahan, coba lagi.",
        });
        return;
      }

      toast.success({
        title: "Catatan tersimpan",
        description: "Transaksi kas berhasil dicatat.",
      });
      setAmount("");
      setNote("");

      // Catatan baru mengubah riwayat, rekap, streak, sekaligus checklist di dashboard.
      queryClient.invalidateQueries({ queryKey: ["pos", "activities"] });
      queryClient.invalidateQueries({ queryKey: ["pos", "summary"] });
      queryClient.invalidateQueries({ queryKey: ["pos", "streak"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: unknown) =>
      toast.error({
        title: "Gagal menyimpan",
        description: errorMessage(e),
      }),
  });

  const submit = () => {
    if (amountError) {
      toast.error({
        title: "Nominal tidak valid",
        description: amountError,
      });
      return;
    }

    createMutation.mutate();
  };

  // Angka streak sepenuhnya dari backend — klien hanya menampilkan.
  const streakPercentage = streak ? Math.min(100, Math.round((streak.current_streak / STREAK_TARGET) * 100)) : 0;

  return (
    <Container maxWidth="7xl" py="40px">
      <VStack alignItems="stretch" gap="24px">
        <Box>
          <h1 className={css({ textStyle: "h6", fontWeight: "bold", color: "secondary.darken8" })}>Catatan Kasir</h1>
          <p className={css({ textStyle: "md", color: "secondary", mt: "8px" })}>
            Catat pemasukan dan pengeluaran harian usaha Anda supaya arus kas selalu terpantau.
          </p>
        </Box>

        <Flex gap="24px" flexDirection={{ base: "column", lg: "row" }} alignItems="stretch">
          {/* ---- Form catat transaksi ---- */}
          <Box {...CARD} flex="1">
            <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8", mb: "24px" })}>
              Catat Transaksi
            </h2>

            <VStack alignItems="stretch" gap="16px">
              <Box>
                <span className={css({ textStyle: "sm", fontWeight: "medium", color: "secondary.darken8" })}>
                  Jenis Transaksi
                </span>
                <HStack gap="12px" mt="8px">
                  {TYPE_OPTIONS.map((option) => {
                    const selected = type === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setType(option.value)}
                        className={css({
                          flex: "1",
                          p: "12px",
                          rounded: "12px",
                          cursor: "pointer",
                          textStyle: "md",
                          fontWeight: selected ? "bold" : "normal",
                          bg: selected ? "#E6F7EE" : "white",
                          color: selected ? "primary" : "secondary.darken8",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: selected ? "primary" : "secondary.lighten2",
                          transition: "all 0.15s ease",
                        })}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </HStack>
              </Box>

              <Box>
                <span className={css({ textStyle: "sm", fontWeight: "medium", color: "secondary.darken8" })}>
                  Nominal
                </span>
                <Input
                  mt="8px"
                  type="text"
                  inputMode="numeric"
                  placeholder="Contoh: 50000"
                  value={amount}
                  onChange={(e) => setAmount(removeMask(e.target.value))}
                />
                <span className={css({ textStyle: "xs", color: "secondary", mt: "4px", display: "block" })}>
                  {amountError ?? normalizeIDR(amountNumber)}
                </span>
              </Box>

              <Box>
                <span className={css({ textStyle: "sm", fontWeight: "medium", color: "secondary.darken8" })}>
                  Catatan (opsional)
                </span>
                <Input
                  mt="8px"
                  type="text"
                  maxLength={NOTE_MAX_LENGTH}
                  placeholder="Contoh: Penjualan kopi 10 cup"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <span className={css({ textStyle: "xs", color: "secondary", mt: "4px", display: "block" })}>
                  {note.length}/{NOTE_MAX_LENGTH} karakter
                </span>
              </Box>

              {/* `loading` hanya kosmetik pada Button, jadi status pending wajib ikut
                  men-disable tombol supaya klik ganda tidak membuat catatan dobel. */}
              <Button
                w="100%"
                onClick={submit}
                loading={createMutation.isPending}
                disabled={!isAmountValid || createMutation.isPending}
              >
                Simpan Catatan
              </Button>
            </VStack>
          </Box>

          {/* ---- Ringkasan ---- */}
          <VStack flex="1" alignItems="stretch" gap="24px">
            {/* Net harian */}
            <Box {...CARD}>
              <HStack justifyContent="space-between" alignItems="flex-start" mb="16px">
                <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8" })}>Net Harian</h2>
                {today && (
                  <span className={css({ textStyle: "sm", color: "secondary" })}>{formatDate(today.date)}</span>
                )}
              </HStack>

              {summaryQuery.isLoading || !today ? (
                <p className={css({ textStyle: "md", color: "secondary" })}>Memuat…</p>
              ) : (
                <VStack alignItems="stretch" gap="12px">
                  <HStack justifyContent="space-between">
                    <span className={css({ textStyle: "md", color: "secondary" })}>Pemasukan</span>
                    <span className={css({ textStyle: "md", fontWeight: "medium", color: "primary" })}>
                      {normalizeIDR(today.income)}
                    </span>
                  </HStack>
                  <HStack justifyContent="space-between">
                    <span className={css({ textStyle: "md", color: "secondary" })}>Pengeluaran</span>
                    <span className={css({ textStyle: "md", fontWeight: "medium", color: "red" })}>
                      {normalizeIDR(today.expense)}
                    </span>
                  </HStack>
                  <Box h="1px" bg="secondary.lighten2" />
                  <HStack justifyContent="space-between">
                    <span className={css({ textStyle: "md", fontWeight: "bold", color: "secondary.darken8" })}>
                      Net Hari Ini
                    </span>
                    <span
                      className={css({
                        textStyle: "lg",
                        fontWeight: "bold",
                        color: today.net < 0 ? "red" : "primary",
                      })}
                    >
                      {normalizeIDR(today.net)}
                    </span>
                  </HStack>
                </VStack>
              )}
            </Box>

            {/* Rekap net per hari — paritas dengan section "Net Harian" di app. */}
            <Box {...CARD}>
              <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8", mb: "16px" })}>
                Rekap Harian
              </h2>

              {summaryQuery.isLoading ? (
                <p className={css({ textStyle: "md", color: "secondary" })}>Memuat…</p>
              ) : daily.length === 0 ? (
                <p className={css({ textStyle: "md", color: "secondary" })}>Belum ada rekap kas harian.</p>
              ) : (
                <VStack alignItems="stretch" gap="12px">
                  {daily.map((day) => (
                    <HStack
                      key={day.date}
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap="16px"
                      className={css({
                        p: "16px",
                        rounded: "12px",
                        borderWidth: "1px",
                        borderStyle: "solid",
                        borderColor: "secondary.lighten2",
                      })}
                    >
                      <Box>
                        <p
                          className={css({
                            textStyle: "sm",
                            fontWeight: "bold",
                            color: "secondary.darken8",
                          })}
                        >
                          {formatDate(day.date)}
                        </p>
                        <p className={css({ textStyle: "xs", color: "secondary", mt: "2px" })}>
                          Masuk {normalizeIDR(day.income)} · Keluar {normalizeIDR(day.expense)}
                        </p>
                      </Box>
                      <span
                        className={css({
                          textStyle: "md",
                          fontWeight: "bold",
                          flexShrink: 0,
                          color: day.net < 0 ? "red" : "primary",
                        })}
                      >
                        {normalizeIDR(day.net)}
                      </span>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>

            {/* Streak */}
            <Box {...CARD}>
              <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8", mb: "16px" })}>
                Streak Mencatat Kas
              </h2>

              {streakQuery.isLoading || !streak ? (
                <p className={css({ textStyle: "md", color: "secondary" })}>Memuat…</p>
              ) : (
                <VStack alignItems="stretch" gap="12px">
                  <Box
                    className={css({
                      alignSelf: "flex-start",
                      bg: "#E6F7EE",
                      color: "primary",
                      px: "16px",
                      py: "8px",
                      rounded: "full",
                      textStyle: "sm",
                      fontWeight: "bold",
                    })}
                  >
                    {streak.current_streak} / {STREAK_TARGET} Hari Aktif Mencatat Kas
                  </Box>

                  <Box
                    className={css({
                      h: "8px",
                      w: "100%",
                      bg: "secondary.lighten2",
                      rounded: "full",
                      overflow: "hidden",
                    })}
                  >
                    <Box
                      style={{ width: `${streakPercentage}%` }}
                      className={css({
                        h: "100%",
                        bg: "primary",
                        rounded: "full",
                        transition: "width 0.3s ease",
                      })}
                    />
                  </Box>

                  <HStack justifyContent="space-between" flexWrap="wrap" gap="8px">
                    <span className={css({ textStyle: "sm", color: "secondary" })}>
                      Streak terpanjang: <b>{streak.longest_streak} hari</b>
                    </span>
                    <span className={css({ textStyle: "sm", color: "secondary" })}>
                      Total catatan: <b>{streak.total_records}</b>
                    </span>
                  </HStack>

                  {streak.last_active_date && (
                    <span className={css({ textStyle: "xs", color: "secondary" })}>
                      Terakhir mencatat: {formatDate(streak.last_active_date)}
                    </span>
                  )}
                </VStack>
              )}
            </Box>
          </VStack>
        </Flex>

        {/* ---- Riwayat catatan ---- */}
        <Box {...CARD}>
          <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8", mb: "24px" })}>
            Riwayat Catatan
          </h2>

          {activitiesQuery.isLoading ? (
            <p className={css({ textStyle: "md", color: "secondary" })}>Memuat…</p>
          ) : activities.length === 0 ? (
            <Center flexDirection="column" gap="8px" py="24px">
              <p className={css({ textStyle: "md", color: "secondary.darken8" })}>Belum ada catatan kas.</p>
              <p className={css({ textStyle: "sm", color: "secondary" })}>
                Mulai catat transaksi pertama Anda melalui form di atas.
              </p>
            </Center>
          ) : (
            <VStack alignItems="stretch" gap="12px">
              {activities.map((activity) => {
                const isIncome = activity.type === "income";
                return (
                  <HStack
                    key={activity._id}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap="16px"
                    p="16px"
                    rounded="12px"
                    borderColor="secondary.lighten2"
                    borderWidth="1px"
                    borderStyle="solid"
                  >
                    <VStack alignItems="flex-start" gap="4px">
                      <span
                        className={css({
                          textStyle: "sm",
                          fontWeight: "bold",
                          color: isIncome ? "primary" : "red",
                        })}
                      >
                        {isIncome ? "Pemasukan" : "Pengeluaran"}
                      </span>
                      {activity.note && (
                        <span className={css({ textStyle: "md", color: "secondary.darken8" })}>{activity.note}</span>
                      )}
                      <span className={css({ textStyle: "xs", color: "secondary" })}>
                        {formatDateTime(activity.createdAt)}
                      </span>
                    </VStack>
                    <span
                      className={css({
                        textStyle: "md",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        color: isIncome ? "primary" : "red",
                      })}
                    >
                      {isIncome ? "+" : "−"} {normalizeIDR(activity.amount)}
                    </span>
                  </HStack>
                );
              })}
            </VStack>
          )}
        </Box>

        <Link to="/">
          <span className={css({ textStyle: "sm", fontWeight: "medium", color: "primary" })}>← Kembali ke Beranda</span>
        </Link>
      </VStack>
    </Container>
  );
};
